import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generic API helper
const api = {
  get: (endpoint, params) => axios.get(`${API_URL}${endpoint}`, { params }),
  post: (endpoint, data, config) => axios.post(`${API_URL}${endpoint}`, data, config),
  put: (endpoint, data) => axios.put(`${API_URL}${endpoint}`, data),
  patch: (endpoint, data) => axios.patch(`${API_URL}${endpoint}`, data),
  delete: (endpoint) => axios.delete(`${API_URL}${endpoint}`),
};

// Authenticated file download helper
const downloadFile = async (url) => {
  try {
    const response = await axios.get(url, { responseType: 'blob' });
    // Check if response is actually an error JSON (e.g. 404)
    if (response.data.type === 'application/json') {
      const text = await response.data.text();
      const err = JSON.parse(text);
      throw new Error(err.detail || 'Download failed');
    }
    const disposition = response.headers['content-disposition'];
    let filename = 'download';
    if (disposition) {
      const match = disposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].replace(/"/g, '');
    }
    const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (e) {
    console.error('Download failed:', e);
    const msg = e.response?.data?.detail || e.message || 'Download failed. The file may not exist yet.';
    window.dispatchEvent(new CustomEvent('download-error', { detail: msg }));
  }
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  list: (params) => api.get('/users', params),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/admin/create-user', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  deactivate: (id) => api.delete(`/admin/users/${id}`),
};

// Depots API
export const depotsAPI = {
  list: () => api.get('/depots'),
  create: (data) => api.post('/depots', data),
};

// Vehicles API
export const vehiclesAPI = {
  list: (params) => api.get('/vehicles', params),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => axios.put(`${API_URL}/vehicles/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/vehicles/${id}`),
  restore: (id) => api.post(`/vehicles/${id}/restore`),
  exportExcel: (params = {}) => {
    let url = `${API_URL}/vehicles/export/excel`;
    const queryParams = [];
    if (params.depot_id) queryParams.push(`depot_id=${params.depot_id}`);
    if (params.vehicle_type) queryParams.push(`vehicle_type=${params.vehicle_type}`);
    if (queryParams.length) url += '?' + queryParams.join('&');
    downloadFile(url);
  },
};

// Trailers API
export const trailersAPI = {
  list: (params) => api.get('/trailers', params),
  create: (data) => api.post('/trailers', data),
};

// Drivers API
export const driversAPI = {
  list: (params) => api.get('/drivers', params),
  get: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => axios.put(`${API_URL}/drivers/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/drivers/${id}`),
  restore: (id) => api.post(`/drivers/${id}/restore`),
  // Chronic Condition
  updateChronicCondition: (driverId, data, userId, userName) =>
    axios.put(`${API_URL}/drivers/${driverId}/chronic-condition?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  // Wellbeing Questionnaires
  listWellbeing: (driverId, limit = 10) => api.get(`/drivers/${driverId}/wellbeing`, { limit }),
  createWellbeing: (driverId, data, userId, userName) =>
    api.post(`/drivers/${driverId}/wellbeing?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  getWellbeingStatus: (driverId) => api.get(`/drivers/${driverId}/wellbeing/status`),
  // PPE Management
  listPPE: (driverId, limit = 10) => api.get(`/drivers/${driverId}/ppe`, { limit }),
  createPPE: (driverId, data, userId, userName) =>
    api.post(`/drivers/${driverId}/ppe?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  getPPEStatus: (driverId) => api.get(`/drivers/${driverId}/ppe/status`),
  // Documents
  listDocuments: (driverId) => api.get(`/drivers/${driverId}/documents`),
  uploadDocument: async (driverId, file, documentType = 'other') => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/drivers/${driverId}/upload?document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (driverId, documentId) => axios.delete(`${API_URL}/drivers/${driverId}/document/${documentId}`),
  // Banners
  getPPEExpiryBanners: () => api.get('/drivers/banners/ppe-expiry'),
  getWellbeingDueBanners: () => api.get('/drivers/banners/wellbeing-due'),
  // Export
  exportExcel: (includeWellbeing = true, includePPE = true) => {
    let url = `${API_URL}/drivers/export/excel?include_wellbeing=${includeWellbeing}&include_ppe=${includePPE}`;
    downloadFile(url);
  },
  // Check expiries and create CAs
  checkExpiries: () => api.post('/drivers/check-expiries'),
};

// SOPs API
export const sopsAPI = {
  list: (params) => api.get('/sops', params),
  get: (code) => api.get(`/sops/${code}`),
  create: (data) => api.post('/sops', data),
  delete: (code) => axios.delete(`${API_URL}/sops/${code}`),
  addVersion: (code, version, content) => 
    api.post(`/sops/${code}/versions?version=${version}&content=${encodeURIComponent(content)}`),
};

// Templates API
export const templatesAPI = {
  list: (params) => api.get('/templates', params),
  get: (code) => api.get(`/templates/${code}`),
  getInspectionItems: (code) => api.get(`/templates/${code}/inspection-items`),
  getData: (code, params) => api.get(`/templates/${code}/data`, params),
  exportData: (code, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_URL}/templates/${code}/data/export?${queryString}`;
  },
};

// Submissions API
export const submissionsAPI = {
  list: (params) => api.get('/submissions', params),
  get: (id) => api.get(`/submissions/${id}`),
  create: (data) => api.post('/submissions', data),
  update: (id, data) => api.patch(`/submissions/${id}`, data),
  submit: (id) => api.post(`/submissions/${id}/submit`),
  approve: (id) => api.post(`/submissions/${id}/approve`),
  reject: (id, reason) => api.post(`/submissions/${id}/reject?reason=${encodeURIComponent(reason)}`),
  return: (id, reason) => api.post(`/submissions/${id}/return?reason=${encodeURIComponent(reason)}`),
  delete: (id) => axios.delete(`${API_URL}/submissions/${id}`),
  restore: (id) => api.post(`/submissions/${id}/restore`),
  exportExcel: (id) => `${API_URL}/submissions/${id}/export/excel`,
  exportPdf: (id) => `${API_URL}/submissions/${id}/export/pdf`,
};

// Export API
export const exportAPI = {
  submissionsList: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return `${API_URL}/export/submissions?${queryString}`;
  },
};

// Expiry API
export const expiryAPI = {
  getSummary: () => api.get('/expiry-summary'),
  runCheck: () => api.post('/admin/run-expiry-check'),
};

// Corrective Actions API
export const correctiveActionsAPI = {
  list: (params) => api.get('/corrective-actions', params),
  create: (data) => api.post('/corrective-actions', data),
  update: (id, data) => api.patch(`/corrective-actions/${id}`, data),
};

// Notifications API
export const notificationsAPI = {
  list: (unreadOnly = false) => api.get('/notifications', { unread_only: unreadOnly }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Audit Logs API
export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  listUsers: () => api.get('/audit-logs/users'),
  exportExcel: (params) => downloadFile(
    `${API_URL}/audit-logs/export?${new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString()}`
  ),
};

// Dashboard API
export const dashboardAPI = {
  driver: () => api.get('/dashboard/driver'),
  supervisor: () => api.get('/dashboard/supervisor'),
  management: () => api.get('/dashboard/management'),
  fleetExpiry: () => api.get('/dashboard/fleet-expiry'),
  inspectionIssues: () => api.get('/dashboard/inspection-issues'),
  serviceAlerts: () => api.get('/dashboard/service-alerts'),
};

// Service Management API
export const serviceAPI = {
  // Fire Extinguishers
  listExtinguishers: (params) => api.get('/fire-extinguishers', params),
  createExtinguisher: (data) => api.post('/fire-extinguishers', data),
  getExtinguisher: (id) => api.get(`/fire-extinguishers/${id}`),
  deleteExtinguisher: (id) => axios.delete(`${API_URL}/fire-extinguishers/${id}`),
  
  // KM Readings
  listKMReadings: (params) => api.get('/km-readings', params),
  createKMReading: (vehicleId, kmReading, readingDate, notes, override = false) => {
    const params = new URLSearchParams({ vehicle_id: vehicleId, km_reading: kmReading });
    if (readingDate) params.append('reading_date', readingDate);
    if (notes) params.append('notes', notes);
    if (override) params.append('override', 'true');
    return api.post(`/km-readings?${params.toString()}`);
  },
  
  // Service Records
  listServiceRecords: (params) => api.get('/service-records', params),
  getServiceRecord: (id) => api.get(`/service-records/${id}`),
  createServiceRecord: (data) => api.post('/service-records', data),
  updateServiceRecord: (id, data) => axios.put(`${API_URL}/service-records/${id}`, data),
  deleteServiceRecord: (id) => axios.delete(`${API_URL}/service-records/${id}`),
  uploadInvoice: async (recordId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/service-records/${recordId}/upload-invoice`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // KM Capture Status
  getKMCaptureStatus: () => api.get('/service/km-capture-status'),
  
  // Daily Checks
  runDailyChecks: () => api.post('/service/run-daily-checks'),
};

// Tyre Survey API
export const tyreSurveyAPI = {
  // Setups
  listSetups: (params) => api.get('/tyre-surveys/setups', params),
  getSetup: (id) => api.get(`/tyre-surveys/setups/${id}`),
  createSetup: (data) => api.post('/tyre-surveys/setups', data),
  updateSetup: (id, data) => axios.put(`${API_URL}/tyre-surveys/setups/${id}`, data),
  deleteSetup: (id) => axios.delete(`${API_URL}/tyre-surveys/setups/${id}`),
  
  // Surveys
  listSurveys: (params) => api.get('/tyre-surveys/', params),
  getSurvey: (id) => api.get(`/tyre-surveys/${id}`),
  createSurvey: (data, userId, userName) => 
    api.post(`/tyre-surveys/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  
  // Dashboard Status
  getCurrentMonthStatus: () => api.get('/tyre-surveys/status/current-month'),
  getTrucksWithoutSetup: () => api.get('/tyre-surveys/status/trucks-without-setup'),
};

// File Upload API
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API_URL}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  
  return response.data;
};

// Breakdowns API
export const breakdownsAPI = {
  list: (params) => api.get('/breakdowns/', params),
  get: (id) => api.get(`/breakdowns/${id}`),
  create: (data, userId, userName) => 
    api.post(`/breakdowns/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data) => axios.put(`${API_URL}/breakdowns/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/breakdowns/${id}`),
  submit: (id, userId, userName) => 
    api.post(`/breakdowns/${id}/submit?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  approve: (id, userId, userName) => 
    api.post(`/breakdowns/${id}/approve?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  return: (id, reason, userId, userName) => 
    api.post(`/breakdowns/${id}/return?reason=${encodeURIComponent(reason)}&user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  getStatistics: () => api.get('/breakdowns/statistics'),
  getPendingApproval: () => api.get('/breakdowns/pending-approval'),
  uploadDocument: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/breakdowns/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcel: (startDate, endDate) => {
    let url = `${API_URL}/breakdowns/export/excel`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    downloadFile(url);
  },
};

// Accidents API
export const accidentsAPI = {
  list: (params) => api.get('/accidents/', params),
  get: (id) => api.get(`/accidents/${id}`),
  create: (data, userId, userName) => 
    api.post(`/accidents/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data) => axios.put(`${API_URL}/accidents/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/accidents/${id}`),
  submit: (id, userId, userName) => 
    api.post(`/accidents/${id}/submit?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  approve: (id, userId, userName) => 
    api.post(`/accidents/${id}/approve?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  return: (id, reason, userId, userName) => 
    api.post(`/accidents/${id}/return?reason=${encodeURIComponent(reason)}&user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  getStatistics: () => api.get('/accidents/statistics'),
  getPendingApproval: () => api.get('/accidents/pending-approval'),
  uploadDocument: async (id, file, docType = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/accidents/${id}/upload?doc_type=${docType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadIncidentStatement: () => downloadFile(`${API_URL}/accidents/templates/incident-statement`),
  downloadFatalityReport: () => downloadFile(`${API_URL}/accidents/templates/fatality-report`),
  exportExcel: (startDate, endDate) => {
    let url = `${API_URL}/accidents/export/excel`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    downloadFile(url);
  },
};

// Fines API
export const finesAPI = {
  list: (params) => api.get('/fines/', params),
  get: (id) => api.get(`/fines/${id}`),
  create: (data, userId, userName) => 
    api.post(`/fines/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data) => axios.put(`${API_URL}/fines/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/fines/${id}`),
  submit: (id, userId, userName) => 
    api.post(`/fines/${id}/submit?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  approve: (id, userId, userName) => 
    api.post(`/fines/${id}/approve?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  return: (id, reason, userId, userName) => 
    api.post(`/fines/${id}/return?reason=${encodeURIComponent(reason)}&user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  markPaid: (id, userId, userName) =>
    api.post(`/fines/${id}/mark-paid?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  getStatistics: () => api.get('/fines/statistics'),
  getPendingApproval: () => api.get('/fines/pending-approval'),
  exportExcel: (startDate, endDate) => {
    let url = `${API_URL}/fines/export/excel`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    downloadFile(url);
  },
};

// Fuel API
export const fuelAPI = {
  list: (params) => api.get('/fuel/', params),
  get: (id) => api.get(`/fuel/${id}`),
  create: (data, userId, userName) => 
    api.post(`/fuel/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data) => axios.put(`${API_URL}/fuel/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/fuel/${id}`),
  getStatistics: (month) => api.get('/fuel/statistics', month ? { month } : {}),
  getLastOdometer: (vehicleId) => api.get(`/fuel/last-odometer/${vehicleId}`),
  getAvailableMonths: () => api.get('/fuel/available-months'),
  uploadSlip: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/fuel/${id}/upload-slip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcel: (params = {}) => {
    let url = `${API_URL}/fuel/export/excel`;
    const queryParams = [];
    if (params.month) queryParams.push(`month=${params.month}`);
    if (params.start_date) queryParams.push(`start_date=${params.start_date}`);
    if (params.end_date) queryParams.push(`end_date=${params.end_date}`);
    if (params.full_history) queryParams.push(`full_history=true`);
    if (queryParams.length) url += '?' + queryParams.join('&');
    downloadFile(url);
  },
};

// Planning & Loading API
export const planningAPI = {
  list: (params) => api.get('/planning/', params),
  get: (id) => api.get(`/planning/${id}`),
  create: (data, userId, userName) => 
    api.post(`/planning/?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data) => axios.put(`${API_URL}/planning/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/planning/${id}`),
  getStatistics: (month) => api.get('/planning/statistics', month ? { month } : {}),
  getAvailableMonths: () => api.get('/planning/available-months'),
  getLoadTypes: () => api.get('/planning/load-types'),
  getRoutes: () => api.get('/planning/routes'),
  getClients: () => api.get('/planning/clients'),
  bulkImport: (records, userId, userName) =>
    api.post(`/planning/bulk-import?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, { records }),
  importExcel: async (file, userId, userName) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/planning/import-excel?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadDocument: async (recordId, file, docType = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    return axios.post(`${API_URL}/planning/${recordId}/upload-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (recordId, documentId) => axios.delete(`${API_URL}/planning/${recordId}/document/${documentId}`),
  downloadTemplate: () => downloadFile(`${API_URL}/planning/template/excel`),
  exportExcel: (params = {}) => {
    let url = `${API_URL}/planning/export/excel`;
    const queryParams = [];
    if (params.month) queryParams.push(`month=${params.month}`);
    if (params.start_date) queryParams.push(`start_date=${params.start_date}`);
    if (params.end_date) queryParams.push(`end_date=${params.end_date}`);
    if (params.full_history) queryParams.push(`full_history=true`);
    if (queryParams.length) url += '?' + queryParams.join('&');
    downloadFile(url);
  },
};

// Replacement Plan API
export const replacementAPI = {
  list: (params) => api.get('/replacement/', params),
  getStatistics: () => api.get('/replacement/statistics'),
  exportExcel: () => downloadFile(`${API_URL}/replacement/export/excel`),
};

// Risk Management API
export const riskManagementAPI = {
  // Baseline
  listBaseline: () => api.get('/risk-management/baseline'),
  getCurrentBaseline: () => api.get('/risk-management/baseline/current'),
  uploadBaseline: (formData) => axios.post(`${API_URL}/risk-management/baseline`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadBaselineTemplate: () => downloadFile(`${API_URL}/risk-management/baseline/template`),
  downloadBaseline: (id) => downloadFile(`${API_URL}/risk-management/baseline/${id}/download`),
  deleteBaseline: (id) => axios.delete(`${API_URL}/risk-management/baseline/${id}`),
  
  // Routes
  getRoutes: () => api.get('/risk-management/routes'),
  getRouteAssessments: (routeName) => api.get(`/risk-management/route/${encodeURIComponent(routeName)}`),
  uploadRouteAssessment: (formData) => axios.post(`${API_URL}/risk-management/route`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadRoute: (id) => downloadFile(`${API_URL}/risk-management/route-assessment/${id}/download`),
  deleteRoute: (id) => axios.delete(`${API_URL}/risk-management/route-assessment/${id}`),
  
  // Statistics
  getStatistics: () => api.get('/risk-management/statistics'),
  
  // Export
  exportBaseline: () => downloadFile(`${API_URL}/risk-management/export/baseline`),
  exportRoutes: () => downloadFile(`${API_URL}/risk-management/export/routes`),
};

// Breathalyzer API
export const breathalyzerAPI = {
  list: (params = {}) => {
    const queryParams = [];
    if (params.month) queryParams.push(`month=${params.month}`);
    if (params.result) queryParams.push(`result=${params.result}`);
    if (params.driver_id) queryParams.push(`driver_id=${params.driver_id}`);
    const query = queryParams.length ? '?' + queryParams.join('&') : '';
    return api.get(`/breathalyzer${query}`);
  },
  get: (id) => api.get(`/breathalyzer/${id}`),
  create: (data, userId, userName) => 
    api.post(`/breathalyzer?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  update: (id, data, userId, userName) =>
    axios.put(`${API_URL}/breathalyzer/${id}?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, data),
  delete: (id) => axios.delete(`${API_URL}/breathalyzer/${id}`),
  // Documents
  uploadDocument: async (testId, file, documentType = 'other') => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/breathalyzer/${testId}/upload?document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listDocuments: (testId) => api.get(`/breathalyzer/${testId}/documents`),
  deleteDocument: (testId, documentId) => axios.delete(`${API_URL}/breathalyzer/${testId}/document/${documentId}`),
  // Banner status
  getBannerStatus: () => api.get('/breathalyzer/banner/status'),
  // Statistics
  getStats: (params = {}) => {
    const queryParams = [];
    if (params.month) queryParams.push(`month=${params.month}`);
    const query = queryParams.length ? '?' + queryParams.join('&') : '';
    return api.get(`/breathalyzer/stats/summary${query}`);
  },
  // Export
  exportExcel: (month) => {
    let url = `${API_URL}/breathalyzer/export/excel`;
    if (month) url += `?month=${month}`;
    downloadFile(url);
  },
};

// Telematics API (Speeding & Fatigue)
export const telematicsAPI = {
  // Upload
  uploadSpeeding: async (file, userId, userName) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/telematics/upload/speeding?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadFatigue: async (file, userId, userName) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/telematics/upload/fatigue?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // List records
  listSpeeding: (params = {}) => {
    const queryParams = [];
    if (params.date) queryParams.push(`date=${params.date}`);
    if (params.registration) queryParams.push(`registration=${params.registration}`);
    if (params.driver_id) queryParams.push(`driver_id=${params.driver_id}`);
    if (params.unassigned_only) queryParams.push(`unassigned_only=true`);
    if (params.has_ca !== undefined) queryParams.push(`has_ca=${params.has_ca}`);
    if (params.speed_min !== undefined) queryParams.push(`speed_min=${params.speed_min}`);
    if (params.upload_id) queryParams.push(`upload_id=${params.upload_id}`);
    if (params.upload_date) queryParams.push(`upload_date=${params.upload_date}`);
    const query = queryParams.length ? '?' + queryParams.join('&') : '';
    return api.get(`/telematics/speeding${query}`);
  },
  listFatigue: (params = {}) => {
    const queryParams = [];
    if (params.date) queryParams.push(`date=${params.date}`);
    if (params.registration) queryParams.push(`registration=${params.registration}`);
    if (params.driver_id) queryParams.push(`driver_id=${params.driver_id}`);
    if (params.unassigned_only) queryParams.push(`unassigned_only=true`);
    if (params.has_violations !== undefined) queryParams.push(`has_violations=${params.has_violations}`);
    if (params.has_ca !== undefined) queryParams.push(`has_ca=${params.has_ca}`);
    if (params.upload_id) queryParams.push(`upload_id=${params.upload_id}`);
    if (params.upload_date) queryParams.push(`upload_date=${params.upload_date}`);
    const query = queryParams.length ? '?' + queryParams.join('&') : '';
    return api.get(`/telematics/fatigue${query}`);
  },
  // List uploads (for filter dropdown)
  listUploads: (reportType = null) => {
    const query = reportType ? `?report_type=${reportType}` : '';
    return api.get(`/telematics/uploads${query}`);
  },
  // Driver assignment
  assignDriverSpeeding: (recordIds, driverId, userId, userName) =>
    api.post(`/telematics/speeding/assign-driver?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      record_ids: recordIds,
      driver_id: driverId
    }),
  assignDriverFatigue: (recordIds, driverId, userId, userName) =>
    api.post(`/telematics/fatigue/assign-driver?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      record_ids: recordIds,
      driver_id: driverId
    }),
  bulkAssignSpeeding: (registration, driverId, startDate, endDate, userId, userName) =>
    api.post(`/telematics/speeding/bulk-assign-driver?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      registration_number: registration,
      driver_id: driverId,
      start_date: startDate,
      end_date: endDate
    }),
  bulkAssignFatigue: (registration, driverId, startDate, endDate, userId, userName) =>
    api.post(`/telematics/fatigue/bulk-assign-driver?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      registration_number: registration,
      driver_id: driverId,
      start_date: startDate,
      end_date: endDate
    }),
  // Process violations
  processSpeedingViolations: (date) => {
    const query = date ? `?date=${date}` : '';
    return api.post(`/telematics/speeding/process-violations${query}`);
  },
  // Banner
  getBannerStatus: () => api.get('/telematics/banner/status'),
  dismissSpeedingBanner: (userId, userName) =>
    api.post(`/telematics/banner/dismiss-speeding?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`),
  // Stats
  getStats: (params = {}) => {
    const query = params.date ? `?date=${params.date}` : '';
    return api.get(`/telematics/stats/summary${query}`);
  },
  // Registrations
  getRegistrations: () => api.get('/telematics/registrations'),
  // Bulk Delete
  bulkDeleteSpeeding: (recordIds, userId, userName) =>
    api.post(`/telematics/speeding/bulk-delete?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      record_ids: recordIds
    }),
  bulkDeleteFatigue: (recordIds, userId, userName) =>
    api.post(`/telematics/fatigue/bulk-delete?user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}`, {
      record_ids: recordIds
    }),
  // Export
  exportSpeeding: (date, registration, exportType, groupByDriver) => {
    let params = new URLSearchParams();
    if (date) params.append('date', date);
    if (registration) params.append('registration', registration);
    if (exportType) params.append('export_type', exportType);
    params.append('group_by_driver', groupByDriver ? 'true' : 'false');
    const queryString = params.toString();
    let url = `${API_URL}/telematics/export/speeding${queryString ? '?' + queryString : ''}`;
    downloadFile(url);
  },
  exportFatigue: (date, registration, exportType, groupByDriver) => {
    let params = new URLSearchParams();
    if (date) params.append('date', date);
    if (registration) params.append('registration', registration);
    if (exportType) params.append('export_type', exportType);
    params.append('group_by_driver', groupByDriver ? 'true' : 'false');
    const queryString = params.toString();
    let url = `${API_URL}/telematics/export/fatigue${queryString ? '?' + queryString : ''}`;
    downloadFile(url);
  },
  // Disciplinary action documents
  uploadSpeedingDisciplinary: (recordId, file, userId, userName, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ user_id: userId, user_name: userName });
    if (notes) params.append('notes', notes);
    return axios.post(
      `${API_URL}/telematics/speeding/${recordId}/disciplinary-upload?${params.toString()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  uploadFatigueDisciplinary: (recordId, file, userId, userName, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ user_id: userId, user_name: userName });
    if (notes) params.append('notes', notes);
    return axios.post(
      `${API_URL}/telematics/fatigue/${recordId}/disciplinary-upload?${params.toString()}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  deleteSpeedingDisciplinary: (recordId, docId) =>
    axios.delete(`${API_URL}/telematics/speeding/${recordId}/disciplinary/${docId}`),
  deleteFatigueDisciplinary: (recordId, docId) =>
    axios.delete(`${API_URL}/telematics/fatigue/${recordId}/disciplinary/${docId}`),
};

// Training API
export const trainingAPI = {
  // Policies
  listPolicies: () => api.get('/training/policies'),
  createPolicy: (name, renewalMonths = 12) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('renewal_months', renewalMonths);
    return axios.post(`${API_URL}/training/policies`, formData);
  },
  deletePolicy: (id) => axios.delete(`${API_URL}/training/policies/${id}`),
  uploadPolicyTemplate: (policyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/training/policies/${policyId}/upload-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadPolicyTemplate: (policyId) => downloadFile(`${API_URL}/training/policies/${policyId}/download-template`),
  downloadAllPolicyTemplates: () => downloadFile(`${API_URL}/training/policies/download-all-templates`),
  uploadSignedPolicy: (policyId, driverId, submissionDate, file) => {
    const formData = new FormData();
    formData.append('driver_id', driverId);
    formData.append('submission_date', submissionDate);
    formData.append('file', file);
    return axios.post(`${API_URL}/training/policies/${policyId}/submissions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listPolicySubmissions: (params = {}) => {
    const qp = [];
    if (params.driver_id) qp.push(`driver_id=${params.driver_id}`);
    if (params.policy_id) qp.push(`policy_id=${params.policy_id}`);
    const q = qp.length ? '?' + qp.join('&') : '';
    return api.get(`/training/policies/submissions${q}`);
  },
  downloadSignedPolicy: (submissionId) => downloadFile(`${API_URL}/training/policies/submissions/${submissionId}/download`),
  deleteSignedPolicy: (submissionId) => axios.delete(`${API_URL}/training/policies/submissions/${submissionId}`),
  exportPoliciesExcel: () => downloadFile(`${API_URL}/training/policies/export/excel`),

  // Reminders
  listReminders: () => api.get('/training/reminders'),
  createReminder: (name, renewalMonths = 6) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('renewal_months', renewalMonths);
    return axios.post(`${API_URL}/training/reminders`, formData);
  },
  deleteReminder: (id) => axios.delete(`${API_URL}/training/reminders/${id}`),
  uploadReminderTemplate: (reminderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/training/reminders/${reminderId}/upload-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadReminderTemplate: (reminderId) => downloadFile(`${API_URL}/training/reminders/${reminderId}/download-template`),
  downloadAllReminderTemplates: () => downloadFile(`${API_URL}/training/reminders/download-all-templates`),
  uploadSignedReminder: (reminderId, driverId, submissionDate, file) => {
    const formData = new FormData();
    formData.append('driver_id', driverId);
    formData.append('submission_date', submissionDate);
    formData.append('file', file);
    return axios.post(`${API_URL}/training/reminders/${reminderId}/submissions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listReminderSubmissions: (params = {}) => {
    const qp = [];
    if (params.driver_id) qp.push(`driver_id=${params.driver_id}`);
    if (params.reminder_id) qp.push(`reminder_id=${params.reminder_id}`);
    const q = qp.length ? '?' + qp.join('&') : '';
    return api.get(`/training/reminders/submissions${q}`);
  },
  downloadSignedReminder: (submissionId) => downloadFile(`${API_URL}/training/reminders/submissions/${submissionId}/download`),
  deleteSignedReminder: (submissionId) => axios.delete(`${API_URL}/training/reminders/submissions/${submissionId}`),
  exportRemindersExcel: () => downloadFile(`${API_URL}/training/reminders/export/excel`),

  // Truck Safety Manual
  listTruckManuals: () => api.get('/training/truck-manuals'),
  confirmTruckManualItem: (truckId, itemType, itemId, file) => {
    const formData = new FormData();
    formData.append('item_type', itemType);
    formData.append('item_id', itemId);
    formData.append('file', file);
    return axios.post(`${API_URL}/training/truck-manuals/${truckId}/confirm`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadManualConfirmation: (confirmationId) => downloadFile(`${API_URL}/training/truck-manuals/confirmation/${confirmationId}/download`),
  deleteManualConfirmation: (confirmationId) => axios.delete(`${API_URL}/training/truck-manuals/confirmation/${confirmationId}`),
  markTruckManualReviewed: (truckId) => api.post(`/training/truck-manuals/${truckId}/review`),
  exportTruckManualsExcel: () => downloadFile(`${API_URL}/training/truck-manuals/export/excel`),

  // Corrective actions & checks
  checkDueItems: () => api.post('/training/check-due-items'),
  listTrainingCAs: () => api.get('/training/corrective-actions'),

  // Training Courses
  listCourses: () => api.get('/training/courses'),
  createCourse: (name, renewalMonths = 12) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('renewal_months', renewalMonths);
    return axios.post(`${API_URL}/training/courses`, formData);
  },
  deleteCourse: (id) => axios.delete(`${API_URL}/training/courses/${id}`),
  uploadCourseTemplate: (courseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/training/courses/${courseId}/upload-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadCourseTemplate: (courseId) => downloadFile(`${API_URL}/training/courses/${courseId}/download-template?_=${Date.now()}`),
  downloadAllCourseTemplates: () => downloadFile(`${API_URL}/training/courses/download-all-templates?_=${Date.now()}`),
  downloadTestTemplate: () => downloadFile(`${API_URL}/training/courses/download-test-template?_=${Date.now()}`),
  uploadTestTemplate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/training/courses/upload-test-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadCourseTestResult: (courseId, driverId, trainingDate, result, file) => {
    const formData = new FormData();
    formData.append('driver_id', driverId);
    formData.append('training_date', trainingDate);
    formData.append('result', result);
    formData.append('file', file);
    return axios.post(`${API_URL}/training/courses/${courseId}/submissions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listCourseSubmissions: (params = {}) => {
    const qp = [];
    if (params.driver_id) qp.push(`driver_id=${params.driver_id}`);
    if (params.course_id) qp.push(`course_id=${params.course_id}`);
    const q = qp.length ? '?' + qp.join('&') : '';
    return api.get(`/training/courses/submissions${q}`);
  },
  downloadCourseTest: (submissionId) => downloadFile(`${API_URL}/training/courses/submissions/${submissionId}/download`),
  deleteCourseSubmission: (submissionId) => axios.delete(`${API_URL}/training/courses/submissions/${submissionId}`),
  exportCoursesExcel: () => downloadFile(`${API_URL}/training/courses/export/excel`),
};

// User Manual API
export const userManualAPI = {
  listSections: () => api.get('/user-manual/sections'),
  getSection: (key) => api.get(`/user-manual/sections/${key}`),
  updateSection: (key, data) => api.put(`/user-manual/sections/${key}`, data),
  downloadSectionPdf: (key) => downloadFile(`${API_URL}/user-manual/sections/${key}/pdf`),
  downloadFullPdf: () => downloadFile(`${API_URL}/user-manual/pdf`),
  viewSectionPdf: (key) => axios.get(`${API_URL}/user-manual/sections/${key}/pdf`, { responseType: 'blob' }),
};

export default api;
